// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
import {
  askPunttOpenItemKindSchema,
  type AskPunttOpenItem,
  type AskPunttOpenItemKind,
} from "@mg/schemas/src/christo/askPunttOpenItems";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { openItemKindLabel } from "./openItemKindLabel";

import { AskV2ReviewCommentSection } from "../review/AskV2ReviewCommentSection";

const KIND_ORDER = askPunttOpenItemKindSchema.options;

/**
 * why: a visible open item is status `open` (cleared rows are filtered out
 * before this list). "Waiting on you" is the subset owned by the viewer.
 * Everything else stays in the open section. Working and Idle are not
 * statuses on an open item, so they are not sections.
 */
function statusSections(
  items: readonly AskPunttOpenItem[],
  currentUserId: string | undefined,
): { key: "waiting-on-you" | "open"; items: AskPunttOpenItem[] }[] {
  const waitingOnYou = items.filter(
    (item) =>
      currentUserId != null &&
      currentUserId.length > 0 &&
      item.ownerUserId === currentUserId,
  );
  const waitingOnYouIds = new Set(waitingOnYou.map((item) => item.id));
  const open = items.filter((item) => !waitingOnYouIds.has(item.id));
  const sections: {
    key: "waiting-on-you" | "open";
    items: AskPunttOpenItem[];
  }[] = [];
  if (waitingOnYou.length > 0) {
    sections.push({ key: "waiting-on-you", items: waitingOnYou });
  }
  if (open.length > 0) {
    sections.push({ key: "open", items: open });
  }
  return sections;
}

function typeGroups(
  items: readonly AskPunttOpenItem[],
): { kind: string; items: AskPunttOpenItem[] }[] {
  const known = new Set<string>(KIND_ORDER);
  // why: a kind the schema does not list yet still has to render, or the row vanishes before the UI learns the name.
  const extraKinds = Array.from(
    new Set(items.map((item) => item.kind).filter((kind) => !known.has(kind))),
  );
  return [...KIND_ORDER, ...extraKinds].flatMap((kind) => {
    const rows = items.filter((item) => item.kind === kind);
    return rows.length > 0 ? [{ kind, items: rows }] : [];
  });
}

export function AskV2OpenItemsGroups({
  items,
  currentUserId,
  renderRows,
}: {
  items: AskPunttOpenItem[];
  currentUserId: string | undefined;
  renderRows: (rows: AskPunttOpenItem[]) => ReactNode;
}) {
  const { t } = useTranslation("ui");
  const [closed, setClosed] = useState<Readonly<Record<string, boolean>>>({});
  const toggle = (key: string) => {
    setClosed((current) => ({ ...current, [key]: current[key] !== true }));
  };
  const labels = {
    "waiting-on-you": t("askV2.openItems.waitingOnYou"),
    open: t("askV2.openItems.statusOpen"),
  } as const;

  return (
    <ul className="flex flex-col" data-testid="ask-v2-open-items-list">
      {statusSections(items, currentUserId).map((section) => (
        <AskV2ReviewCommentSection
          key={section.key}
          sectionKey={section.key}
          label={labels[section.key]}
          count={section.items.length}
          open={closed[section.key] !== true}
          listId={`ask-v2-open-items-section-${section.key}`}
          onToggle={() => toggle(section.key)}
          heading="parent"
        >
          {typeGroups(section.items).map((group) => {
            const key = `${section.key}-${group.kind}`;
            return (
              <AskV2ReviewCommentSection
                key={key}
                sectionKey={key}
                label={openItemKindLabel(group.kind as AskPunttOpenItemKind, t)}
                count={group.items.length}
                open={closed[key] !== true}
                listId={`ask-v2-open-items-section-${key}`}
                onToggle={() => toggle(key)}
                heading="subcategory"
              >
                {renderRows(group.items)}
              </AskV2ReviewCommentSection>
            );
          })}
        </AskV2ReviewCommentSection>
      ))}
    </ul>
  );
}
